/**
 * Vault wrapper format (`.wult` JSON envelope).
 *
 * This module owns the on-disk shape of WINBIT32 vault files and is
 * intentionally free of dependencies on the Vultisig SDK or any WASM
 * binary so it can be exercised by unit tests directly. The Vultisig
 * service (`vultisig.js`) re-exports the public surface for callers
 * that still import from there — the canonical home for new code is
 * here.
 *
 * On-disk shape (v2):
 *
 *   {
 *     "type": "winbit32-vault-v2",
 *     "version": 2,
 *     "vaultData": "<base64 Vultisig SDK protobuf export>",
 *     "encrypted": <bool>,
 *     "crossChainIdentity"?: { "hexChainCode", "publicKeyEcdsa" },
 *     "frost": {
 *       "orchard"?: <JSON string | PBKDF2+AES-GCM blob>,
 *       "sapling"?: <base64 string | PBKDF2+AES-GCM blob>,
 *       "monero"?:  <JSON string | PBKDF2+AES-GCM blob>
 *     }
 *   }
 *
 * Each FROST payload sits in its own field so a schema change to one
 * chain's bundle doesn't invalidate the others. When a password is
 * given each payload is encrypted independently via PBKDF2-SHA-256
 * (600 000 iters) + AES-GCM-256; the salt and IV are bundled into the
 * envelope as `salt.iv.ciphertext` (each base64).
 *
 * Monero bundles arrive from `fromtFrostVaultIntegration` as
 * `MoneroKeyShareBundle` — a mix of `Uint8Array`, `BigInt` and
 * strings. Direct JSON.stringify would lose the binary parts, so we
 * shadow it as `StoredFromtBundle` (base64 + decimal-string BigInt)
 * inside the envelope and reconstitute on unwrap.
 */

export const VAULT_V2_TYPE = 'winbit32-vault-v2';
export const VAULT_V2_VERSION = 2;
const FROST_KDF_ITER = 600_000;

// ─── base64 / binary helpers ────────────────────────────────────────

function _vB64Enc(buf) {
	return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

// Returns a `Uint8Array` rather than the raw `ArrayBuffer` — both
// browsers and Node's webcrypto accept Uint8Array directly, whereas
// some Web Crypto polyfills (e.g. the one bundled with jsdom for
// tests) reject bare `ArrayBuffer` instances passed as ciphertext.
function _vB64Dec(str) {
	const bin = atob(str);
	const a = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
	return a;
}

// ─── PBKDF2 + AES-GCM payload encryption ────────────────────────────

async function _frostEncrypt(jsonStr, password) {
	const enc = new TextEncoder();
	const salt = crypto.getRandomValues(new Uint8Array(16));
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const km = await crypto.subtle.importKey(
		'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey'],
	);
	const key = await crypto.subtle.deriveKey(
		{ name: 'PBKDF2', salt, iterations: FROST_KDF_ITER, hash: 'SHA-256' },
		km, { name: 'AES-GCM', length: 256 }, false, ['encrypt'],
	);
	const ct = await crypto.subtle.encrypt(
		{ name: 'AES-GCM', iv }, key, enc.encode(jsonStr),
	);
	return _vB64Enc(salt) + '.' + _vB64Enc(iv) + '.' + _vB64Enc(ct);
}

async function _frostDecrypt(encoded, password) {
	const [sB, iB, cB] = encoded.split('.');
	if (!sB || !iB || !cB) throw new Error('Malformed encrypted FROST blob');
	const km = await crypto.subtle.importKey(
		'raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey'],
	);
	const key = await crypto.subtle.deriveKey(
		{
			name: 'PBKDF2',
			salt: _vB64Dec(sB),
			iterations: FROST_KDF_ITER,
			hash: 'SHA-256',
		},
		km, { name: 'AES-GCM', length: 256 }, false, ['decrypt'],
	);
	const plain = await crypto.subtle.decrypt(
		{ name: 'AES-GCM', iv: _vB64Dec(iB) },
		key,
		_vB64Dec(cB),
	);
	return new TextDecoder().decode(plain);
}

// ─── Monero bundle shape conversion ─────────────────────────────────
//
// `MoneroKeyShareBundle` (the in-memory shape from fromt-wasm):
//   { partyName, frostId, network, birthday: BigInt,
//     bundleBytes: Uint8Array, bakedAtCreation, ceremonyVersion }
//
// `StoredFromtBundle` (the JSON-safe shape used both inside .wult
// files and by `fromtFrostVaultIntegration` for localStorage):
//   { partyName, frostId, network, birthday: string,
//     bundleBase64: string, bakedAtCreation, ceremonyVersion }
//
// Keeping the same wire shape on both sides means a `.wult` import
// can drop the bundle straight into localStorage without massaging.

function _moneroBundleToStored(bundle) {
	if (!bundle) return null;
	const bytes = bundle.bundleBytes instanceof Uint8Array
		? bundle.bundleBytes
		: new Uint8Array(bundle.bundleBytes || []);
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
	return {
		partyName: bundle.partyName,
		frostId: bundle.frostId,
		network: bundle.network || 'mainnet',
		birthday: (typeof bundle.birthday === 'bigint'
			? bundle.birthday
			: BigInt(bundle.birthday || 0)).toString(),
		bundleBase64: btoa(bin),
		bakedAtCreation: bundle.bakedAtCreation !== false,
		ceremonyVersion: bundle.ceremonyVersion || 1,
	};
}

function _moneroStoredToBundle(stored) {
	if (!stored || typeof stored.bundleBase64 !== 'string') return null;
	const bin = atob(stored.bundleBase64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
	return {
		partyName: stored.partyName,
		frostId: stored.frostId,
		network: stored.network || 'mainnet',
		birthday: BigInt(stored.birthday || '0'),
		bundleBytes: bytes,
		bakedAtCreation: stored.bakedAtCreation !== false,
		ceremonyVersion: stored.ceremonyVersion || 1,
	};
}

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Detect whether content is a v2 vault wrapper with embedded FROST data.
 *
 * Cheap structural sniff — only inspects the first byte and the parsed
 * `type` field, so a malformed file or a raw Vultisig protobuf both
 * return `false` without throwing.
 *
 * @param {unknown} content
 * @returns {boolean}
 */
export function isWrappedVault(content) {
	if (typeof content !== 'string' || content[0] !== '{') return false;
	try { return JSON.parse(content)?.type === VAULT_V2_TYPE; }
	catch { return false; }
}

/**
 * Wrap an SDK vault export string with FROST key material.
 *
 * Accepted FROST payloads (any combination, all optional):
 *   - Orchard (Zcash): `orchardFrost` — WINBIT32 OrchardKeyBundle shape.
 *   - Sapling (Zcash): `saplingBundleBase64` — opaque base64 string.
 *   - Monero: `moneroFrost` — MoneroKeyShareBundle from
 *     `fromtFrostVaultIntegration`. The Uint8Array bundle bytes are
 *     serialised as base64 + the BigInt `birthday` as a decimal string
 *     so the resulting object survives JSON.stringify.
 *
 * If password is given each FROST payload is PBKDF2+AES-GCM encrypted
 * independently. Keeping them in separate fields means a future schema
 * change to one chain's bundle doesn't invalidate the others.
 *
 * @param {{
 *   sdkExportData: string,
 *   orchardFrost?: object|null,
 *   moneroFrost?: object|null,
 *   password?: string,
 *   crossChainIdentity?: { hexChainCode: string, publicKeyEcdsa: string }|null,
 *   saplingBundleBase64?: string|null,
 * }} opts
 * @returns {Promise<string>} JSON string for the .wult file
 */
export async function wrapVaultWithFrost({
	sdkExportData,
	orchardFrost,
	moneroFrost = null,
	password,
	crossChainIdentity = null,
	saplingBundleBase64 = null,
}) {
	const frost = {};
	if (orchardFrost) {
		const json = JSON.stringify(orchardFrost);
		frost.orchard = password ? await _frostEncrypt(json, password) : json;
	}
	if (saplingBundleBase64) {
		frost.sapling = password
			? await _frostEncrypt(saplingBundleBase64, password)
			: saplingBundleBase64;
	}
	if (moneroFrost) {
		const stored = _moneroBundleToStored(moneroFrost);
		const json = JSON.stringify(stored);
		frost.monero = password ? await _frostEncrypt(json, password) : json;
	}
	const payload = {
		type: VAULT_V2_TYPE,
		version: VAULT_V2_VERSION,
		vaultData: sdkExportData,
		frost,
		encrypted: !!password,
	};
	if (crossChainIdentity?.hexChainCode && crossChainIdentity?.publicKeyEcdsa) {
		payload.crossChainIdentity = {
			hexChainCode: crossChainIdentity.hexChainCode,
			publicKeyEcdsa: crossChainIdentity.publicKeyEcdsa,
		};
	}
	return JSON.stringify(payload);
}

/**
 * Unwrap a v2 vault wrapper, returning the SDK vault string + FROST data.
 *
 * The returned `moneroFrost` (when present) is a fully-reconstituted
 * `MoneroKeyShareBundle` — `bundleBytes` is a `Uint8Array` and
 * `birthday` is a `BigInt`, matching the shape produced by
 * `fromtFrostVaultIntegration.generateFromtBundlesForFreshVault`.
 *
 * Individual FROST payload decryption is wrapped in try/catch so a
 * corrupted Sapling blob (for example) doesn't lose the Orchard or
 * Monero share alongside it. The corresponding result field stays
 * `null` and a console warning is emitted.
 *
 * @param {string} content
 * @param {string} [password]
 * @returns {Promise<{
 *   sdkVaultString: string,
 *   orchardFrost: object|null,
 *   moneroFrost: object|null,
 *   crossChainIdentity: { hexChainCode: string, publicKeyEcdsa: string }|null,
 *   saplingBundleBase64: string|null,
 * }>}
 */
export async function unwrapVault(content, password) {
	const obj = JSON.parse(content);
	if (obj?.type !== VAULT_V2_TYPE) throw new Error('Not a v2 vault wrapper');
	const result = {
		sdkVaultString: obj.vaultData,
		orchardFrost: null,
		moneroFrost: null,
		crossChainIdentity: null,
		saplingBundleBase64: null,
	};
	if (obj.crossChainIdentity?.hexChainCode && obj.crossChainIdentity?.publicKeyEcdsa) {
		result.crossChainIdentity = {
			hexChainCode: obj.crossChainIdentity.hexChainCode,
			publicKeyEcdsa: obj.crossChainIdentity.publicKeyEcdsa,
		};
	}
	if (obj.frost?.orchard) {
		try {
			const raw = (obj.encrypted && password)
				? await _frostDecrypt(obj.frost.orchard, password)
				: obj.frost.orchard;
			result.orchardFrost = typeof raw === 'string' ? JSON.parse(raw) : raw;
		} catch (err) {
			console.warn('Orchard FROST data extraction failed:', err?.message);
		}
	}
	if (obj.frost?.sapling) {
		try {
			const raw = (obj.encrypted && password)
				? await _frostDecrypt(obj.frost.sapling, password)
				: obj.frost.sapling;
			result.saplingBundleBase64 = typeof raw === 'string' ? raw : null;
		} catch (err) {
			console.warn('Sapling FROST data extraction failed:', err?.message);
		}
	}
	if (obj.frost?.monero) {
		try {
			const raw = (obj.encrypted && password)
				? await _frostDecrypt(obj.frost.monero, password)
				: obj.frost.monero;
			const stored = typeof raw === 'string' ? JSON.parse(raw) : raw;
			result.moneroFrost = _moneroStoredToBundle(stored);
		} catch (err) {
			console.warn('Monero FROST data extraction failed:', err?.message);
		}
	}
	return result;
}

// Exported for tests; not part of the public API.
export const __test_internals = {
	_moneroBundleToStored,
	_moneroStoredToBundle,
	_frostEncrypt,
	_frostDecrypt,
};
